import { useEffect } from 'react';
import { useLanguage } from '../i18n';

/**
 * SEO Head 组件
 * 动态管理页面的 title、meta 标签、Open Graph 和结构化数据
 * @param {string} page - 当前页面：'json' | 'timestamp' | 'gostruct' | null (首页)
 * @param {object} article - 博客文章数据（可选）
 * @param {string} siteUrl - 网站 URL
 */
function SEOHead({ page = null, article = null, siteUrl = 'https://devtoolkit.top' }) {
  const { t, language } = useLanguage();

  useEffect(() => {
    // 获取当前页面的 SEO 配置
    const getSeoConfig = () => {
      // 博客文章
      if (article) {
        return {
          title: article.title[language] + ' | DevToolkit',
          description: article.description[language],
          keywords: article.keywords,
          type: 'article',
        };
      }
      // 工具页面
      if (page && ['json', 'timestamp', 'gostruct'].includes(page)) {
        return {
          title: t(`seo.${page}.title`),
          description: t(`seo.${page}.description`),
          keywords: t(`seo.${page}.keywords`),
          type: 'website',
        };
      }
      // 首页
      return {
        title: t('seo.site.title'),
        description: t('seo.site.description'),
        keywords: t('seo.site.keywords'),
        type: 'website',
      };
    };

    const config = getSeoConfig();

    // 更新 title
    document.title = config.title;

    // 更新或创建 meta 标签
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // 基础 meta 标签
    updateMeta('description', config.description);
    updateMeta('keywords', config.keywords);
    updateMeta('author', 'DevToolkit');
    updateMeta('robots', 'index, follow');
    updateMeta('language', language === 'zh' ? 'zh-CN' : 'en');

    // Open Graph 标签
    updateMeta('og:title', config.title, true);
    updateMeta('og:description', config.description, true);
    updateMeta('og:type', config.type || 'website', true);
    updateMeta('og:site_name', t('seo.og.siteName'), true);
    updateMeta('og:url', article ? `${siteUrl}/blog/${article.slug}` : siteUrl, true);
    updateMeta('og:locale', language === 'zh' ? 'zh_CN' : 'en_US', true);
    // 文章特定的 OG 标签
    if (article) {
      updateMeta('article:published_time', article.date, true);
      updateMeta('article:author', article.author, true);
      updateMeta('article:section', article.category[language], true);
    }
    // updateMeta('og:image', `${siteUrl}/og-image.png`, true);
    // updateMeta('og:image:alt', t('seo.og.imageAlt'), true);

    // Twitter Card 标签
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', config.title);
    updateMeta('twitter:description', config.description);
    // updateMeta('twitter:image', `${siteUrl}/og-image.png`);

    // 更新结构化数据 (JSON-LD)
    const updateStructuredData = () => {
      let script = document.querySelector('script[type="application/ld+json"]');
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }

      let structuredData;

      // 博客文章的结构化数据
      if (article) {
        structuredData = {
          '@context': 'https://schema.org',
          '@type': 'Article',
          'headline': article.title[language],
          'description': article.description[language],
          'image': `${siteUrl}/logo512.png`,
          'author': {
            '@type': 'Organization',
            'name': article.author,
          },
          'publisher': {
            '@type': 'Organization',
            'name': 'DevToolkit',
            'logo': {
              '@type': 'ImageObject',
              'url': `${siteUrl}/logo192.png`,
            },
          },
          'datePublished': article.date,
          'dateModified': article.date,
          'mainEntityOfPage': {
            '@type': 'WebPage',
            '@id': `${siteUrl}/blog/${article.slug}`,
          },
          'keywords': article.keywords,
          'articleSection': article.category[language],
          'inLanguage': language === 'zh' ? 'zh-CN' : 'en',
        };
      } else {
        // Web Application 的结构化数据
        structuredData = {
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          'name': t('seo.structured.appName'),
          'description': t('seo.structured.appDescription'),
          'url': siteUrl,
          'applicationCategory': t('seo.structured.appCategory'),
          'operatingSystem': 'Any',
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'USD',
          },
          'featureList': [
            t('seo.structured.features.jsonFormatter'),
            t('seo.structured.features.jsonTable'),
            t('seo.structured.features.jsonTree'),
            t('seo.structured.features.innerJson'),
            t('seo.structured.features.multiTab'),
            t('seo.structured.features.smartFix'),
            t('seo.structured.features.goStructConvert'),
            t('seo.structured.features.timestampConvert'),
          ],
          'inLanguage': language === 'zh' ? 'zh-CN' : 'en',
          'author': {
            '@type': 'Organization',
            'name': 'DevToolkit',
          },
        };
      }

      script.textContent = JSON.stringify(structuredData);
    };

    updateStructuredData();

    // 更新 html lang 属性
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : 'en';

  }, [page, article, language, t, siteUrl]);

  // 这个组件不渲染任何 UI
  return null;
}

export default SEOHead;
